/* eslint-disable no-underscore-dangle */
/* eslint-disable no-param-reassign */
import React from 'react';
import { Box, Image } from '@havenengineering/module-haven-content';
import { getCaravanSizeByGrade } from 'module/shared/helper/get-caravan-size-by-grade';
import { SearchResultCard } from '../../../shared/component/search-result-card-redesign';
import AccommodationFeaturePills from '../accommodation-feature-pills';
import { getFeaturePillsForGrade } from '../../../shared/helper/get-feature-pills-for-grade';
import CardHeader from '../card-header';
import GalleryPreviewRedesign from '../../../shared/component/search-result-card-redesign/gallery-preview-redesign';
import { HandleAccommodationGalleryGeneral } from '../../../shared/hook/use-accommodation-gallery/use-accommodation-gallery';
import styles from './aggregating-result-card-container.module.scss';
import { Query } from '../../../../types/v2/holiday-search';
import {
  AccommodationSleeps,
  AggregatedAccommodationType,
  SleepingArrangements,
} from '../../type/types';
import { getPriceForGradePage } from '../../helper/get-price-for-grade-page';
import { HighlightedCardVariants } from '../../../shared/component/search-result-card/highlighted-card';
import AvailabilityLabel from '../availability-label';
import { getAvailabilityLabelAlternate } from '../../helper/get-availability-label';
import { useUnleash } from '../../../../providers/unleashProvider';
import { BaseFeature } from '../../../../types/accommodation-features';
import { SelectedItem } from '../../../../types/aggregating';
import { useLabelCallback } from '../../../shared/hook/use-label-callback/use-label-callback';

type Props = {
  item: AggregatedAccommodationType & {
    accommodationFeatures?: { features: BaseFeature[]; highlights: string[] };
    caravanFeatures?: Box[] | string;
    caravanImages?: Image[] | string;
  };
  HandleAccommodationGalleryGeneral: HandleAccommodationGalleryGeneral;
  query: Query;
  isSoldOut?: boolean;
  recommended?: boolean;
  setSelection: (selection: SelectedItem) => void;
  selectedGrade: string;
  isActive?: boolean;
};

export const AggregatingResultCardContainer = ({
  item,
  HandleAccommodationGalleryGeneral,
  query,
  isSoldOut,
  recommended,
  setSelection,
  selectedGrade,
  isActive,
}: Props) => {
  const { isUnleashVariantActiveCsr } = useUnleash();
  const { labelCallBack, availabilityIndicatorLabel } = useLabelCallback();

  const showDepositPills = isUnleashVariantActiveCsr(
    'search-424-deposit-pills-on-grade-page',
    '1',
    query,
  );

  const features: Box[] =
    typeof item.caravanFeatures === 'string'
      ? JSON.parse(item.caravanFeatures)
      : item.caravanFeatures;

  const caravanFeatures = features.map((feature) => ({
    ...feature,
    simple: true,
    presentation: true,
  }));

  const { featurePills } = getFeaturePillsForGrade({
    caravanFeatures,
    grade: item.baseGradeCode,
    allFeatures: true,
  });

  const caravanImages: Image[] =
    typeof item.caravanImages === 'string' ? JSON.parse(item.caravanImages) : item.caravanImages;

  const { sleepingArrangements } = item;

  const numberOfBedrooms = sleepingArrangements
    ?.map((sleepingArrangement: SleepingArrangements) => sleepingArrangement.bedrooms)
    .join('/');

  const cabinCapacity = sleepingArrangements
    ?.map((sleepingArrangement: SleepingArrangements) =>
      sleepingArrangement.sleeps.map((sleep: AccommodationSleeps) => sleep.sleeps).join('/'),
    )
    .join('/');

  const urgencyMessage = getAvailabilityLabelAlternate(item, isSoldOut);

  const discountedPrice =
    item.partner?.leadingPrice && item.partner?.leadingPrice < item.leadingPrice
      ? item.partner.leadingPrice
      : undefined;

  return (
    <div className={styles.aggregatingResultCardContainer}>
      <SearchResultCard
        galleryPreview={
          <GalleryPreviewRedesign
            item={{ ...item, caravanImages }}
            handleAccommodationGallery={HandleAccommodationGalleryGeneral}
            aggregatedSearch
            highlighted={recommended}
            soldOut={isSoldOut}
            showDepositPills={showDepositPills}
          />
        }
        accommodationDescription={item.accommodationDescription}
        gridLayout
        highlightedVariant={recommended ? HighlightedCardVariants.Recommended : undefined}
        highlightedText={recommended ? 'Recommended' : undefined}
        setSelection={setSelection}
        selectedGrade={selectedGrade}
        gradeCode={item.baseGradeCode || ''}
        leadingPrice={item.leadingPrice}
        partnerLeadingPrice={item.partner?.leadingPrice}
        available={item.available}
        occupancyData={item?.occupancy}
        availabilityLabel={availabilityIndicatorLabel}
      >
        {urgencyMessage && !item?.occupancy && (
          <AvailabilityLabel availabilityLabel={urgencyMessage} />
        )}
        <CardHeader
          grade={item.accommodationDescription || ''}
          size={getCaravanSizeByGrade(item.accommodationSubType)}
          occupancyData={item?.occupancy}
          labelCallBack={labelCallBack}
          isSoldOut={isSoldOut}
          isActive={isActive}
        />
        <AccommodationFeaturePills
          numberOfBedrooms={numberOfBedrooms || ''}
          cabinCapacity={cabinCapacity || ''}
          v1AccommodationFeatures={featurePills?.features || []}
          v2AccommodationFeatures={item?.accommodationFeatures?.features || []}
          v2AccommodationFeatureHighlights={item?.accommodationFeatures?.highlights || []}
          price={getPriceForGradePage(item.leadingPrice, isSoldOut)}
          discountedPrice={discountedPrice}
        />
      </SearchResultCard>
    </div>
  );
};
